import React, { Fragment, PureComponent } from 'react';
import PropTypes from 'prop-types';
import { injectStyles } from 'injectStyles';
import classNames from 'classnames';

import { getResamplingQuery, getURLChainingParameter } from 'util/resampling';
import getMobileLightVariant from 'util/getMobileLightVariant';
import { LABEL } from 'globalConst/const';

import SocialShare from 'components/SocialShare';
import ButtonTypes from 'components/Button/Button.const';
import * as cssVariable from 'cssVariables';

import Title from './Title';
import CONTENT_TYPES from 'globalConst/contentTypes-const';
import ToggleFavoriteButton from 'components/ToggleFavoriteButton';

class PodcastDetailPageHeader extends PureComponent {
  static defaultProps = {
    contentWidth: null,
    mainPodcastImage: null,
    title: null,
    author: null,
    description: null,
    id: null,
    slug: null,
  };

  static propTypes = {
    contentWidth: PropTypes.number,
    classes: PropTypes.object.isRequired,
    mainPodcastImage: PropTypes.object,
    isFavorite: PropTypes.bool.isRequired,
    title: PropTypes.string,
    description: PropTypes.string,
    author: PropTypes.string,
    id: PropTypes.string,
    slug: PropTypes.string,
  };

  state = {
    showFullDescription: false,
    descriptionIsOverflowing: false,
  };

  componentDidUpdate(prevProps) {
    const { description, contentWidth } = this.props;
    if (prevProps.description !== description || prevProps.contentWidth !== contentWidth) {
      this.isDescriptionOverflowing();
    }
  }

  mobileLightVariant = getMobileLightVariant();

  renderDescElement = () => {
    const { showFullDescription } = this.state;
    const { author, description, classes, title } = this.props;
    return (
      <Fragment>
        {title && this.mobileLightVariant && (
          <div data-testid="podcast-details-page-header-title" className={classes.podcastHeaderTitle}>
            {title}
          </div>
        )}
        {author && (
          <div
            data-testid="podcast-details-page-header-subtitle"
            className={classNames(classes.podcastHeaderSubtitle, {
              [classes.podcastHeaderSubtitleMobile]: this.mobileLightVariant,
            })}
          >
            {author}
          </div>
        )}
        {description && (
          <div
            data-testid="podcast-details-page-header-description"
            className={classNames(classes.podcastHeaderDescription, {
              [classes.overflowEllipsis]: !showFullDescription,
            })}
            ref={(el) => {
              this.descriptionDomElement = el;
            }}
            dangerouslySetInnerHTML={{ __html: description }}
          />
        )}
      </Fragment>
    );
  };

  getShareDescription = () => {
    const { title } = this.props;
    const url = window.location.href;
    return `Luister de laatste aflevering van ${title} via ${url}`;
  };

  toggleDescriptionCollapse = () => {
    this.setState((prevState) => ({
      showFullDescription: !prevState.showFullDescription,
    }));
  };

  isDescriptionOverflowing = () => {
    const element = this.descriptionDomElement;
    if (element) {
      this.setState({
        descriptionIsOverflowing: element.offsetHeight < element.scrollHeight,
      });
    }
  };

  renderDesktopHeader = (hasImages) => {
    const { classes, id, title, isFavorite, slug } = this.props;
    const { descriptionIsOverflowing, showFullDescription } = this.state;
    return (
      <div className={classes.headerContent}>
        <Title title={title} />
        <div className={classes.wrapper}>
          <div className={classNames(classes.flex)}>
            <div
              data-testid="podcast-details-page-header-background-image"
              className={classes.image}
              style={{ backgroundImage: hasImages }}
            />
            <div className={classes.podcastHeaderInfo}>
              <div className={classNames(classes.flex, classes.actionsHolder)}>
                <div className={classes.buttonWrapper}>
                  <ToggleFavoriteButton
                    type={ButtonTypes.PRIMARY}
                    item={{ id, contentTypeId: CONTENT_TYPES.PODCAST, title, slug }}
                    text={isFavorite ? 'Toegevoeged' : 'Toevoegen'}
                  />
                </div>
                <div className={classes.Shareholder}>
                  <SocialShare subject={title} description={this.getShareDescription()} color={cssVariable.grey500} />
                </div>
              </div>
              <div
                className={classNames(classes.descriptionHolder, {
                  [classes.fullDescription]: showFullDescription,
                })}
              >
                {this.renderDescElement()}
                {descriptionIsOverflowing && (
                  <button
                    data-testid="podcast-details-page-header-show-more"
                    type="button"
                    className={classes.toggleBtn}
                    onClick={this.toggleDescriptionCollapse}
                  >
                    {showFullDescription ? LABEL.HIDE : LABEL.SHOW_MORE}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  renderMobileHeader = (hasImages) => {
    const { classes, author, description } = this.props;
    const { showFullDescription, descriptionIsOverflowing } = this.state;

    return (
      <Fragment>
        <div className={classes.containerMobile}>
          <div
            data-testid="podcast-details-page-header-mobile-background-image"
            className={classes.backgroundImage}
            style={{ backgroundImage: hasImages }}
          />
          <div
            data-testid="podcast-details-page-header-mobile-tile-image"
            className={classes.tileImage}
            style={{ backgroundImage: hasImages }}
          />
        </div>
        <div
          className={classNames(classes.descriptionContainer, {
            [classes.fullDescription]: showFullDescription,
          })}
        >
          {this.renderDescElement(author, description, classes)}
          {descriptionIsOverflowing && (
            <button
              data-testid="podcast-details-page-header-mobile-show-more"
              type="button"
              className={classes.toggleCollapseBtn}
              onClick={this.toggleDescriptionCollapse}
            >
              {showFullDescription ? LABEL.HIDE : LABEL.SHOW_MORE}
            </button>
          )}
        </div>
      </Fragment>
    );
  };

  render() {
    const { mainPodcastImage } = this.props;

    const hasImages =
      mainPodcastImage &&
      mainPodcastImage.url &&
      `url(${mainPodcastImage.url}${getURLChainingParameter(mainPodcastImage.url)}${getResamplingQuery()})`;

    return (
      <header data-testid="podcast_detail_page_header">
        {this.mobileLightVariant ? this.renderMobileHeader(hasImages) : this.renderDesktopHeader(hasImages)}
      </header>
    );
  }
}
const thumbDimensions = {
  DESKTOP: 252,
  DESKTOP_MEDIUM: 197,
  MOBILE: 106,
};

const styles = (theme) => ({
  headerContent: {
    padding: `0 40px ${cssVariable.gutter}px 40px`,
  },
  flex: {
    display: 'flex',
  },
  podcastHeaderInfo: {
    flex: 1,
  },
  hideDownSm: {
    display: 'block',
  },
  showDownSm: {
    display: 'none',
  },
  wrapper: {
    margin: [40, 0, 20],
  },
  podcastTitle: {
    ...cssVariable.pageTitle,
    marginBottom: 40,
  },
  buttonWrapper: {
    marginRight: 20,

    '& svg': {
      width: 25,
      height: 25,
    },
  },
  Shareholder: {
    width: 35,
    alignSelf: 'center',
  },
  image: {
    flex: `0 0 ${thumbDimensions.DESKTOP}px`,
    height: thumbDimensions.DESKTOP,
    marginRight: 40,
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundColor: cssVariable.grey100,
    borderRadius: 5.26,
  },
  podcastHeaderTitle: {
    color: cssVariable.grey600,
    fontWeight: cssVariable.fontWeights.bold,
    marginBottom: 2,
  },
  podcastHeaderSubtitle: {
    ...cssVariable.headerText,
    color: cssVariable.grey500,
    fontSize: cssVariable.fontSizes.medium,
    fontWeight: cssVariable.fontWeights.bold,
    margin: [30, 0, 10, 0],
  },

  toggleBtn: {
    width: '70%',
    height: 40,
    color: cssVariable.green500,
    background: cssVariable.white,
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    textAlign: 'left',
  },

  podcastHeaderDescription: {
    ...cssVariable.headerText,
    ...cssVariable.headerDescription,
    '& p': {
      marginBlockStart: 0,
    },
    '& a': {
      fontWeight: cssVariable.fontWeights.bold,
    },
  },
  descriptionHolder: {
    position: 'relative',
    fontSize: cssVariable.fontSizes.medium,
    color: cssVariable.grey600,

    '& $overflowEllipsis': {
      '@supports (-webkit-line-clamp: 3)': {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'initial',
        display: '-webkit-box',
        [cssVariable.WEBKIT_LINE_CLAMP]: 3,
        [cssVariable.WEBKIT_BOX_ORIENT]: 'vertical',
      },
      // IE fix
      height: '3.99em',
      overflow: 'hidden',
      position: 'relative',
      '&:after': {
        content: '...',
        textAlign: 'right',
        position: 'absolute',
        bottom: 0,
        right: 0,
        width: '70%',
        background: 'linear-gradient(to right, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1) 50%)',
      },
    },
  },
  buttonHolder: {
    borderRadius: 4,
    overflow: 'hidden',
    '& button': {
      padding: [0, 10],
      cursor: 'pointer',
    },
    // IE fix
    '& svg': {
      marginRight: 10,
    },
  },
  [theme.breakpoints.down('ml')]: {
    image: {
      marginRight: 20,
      flex: `0 0 ${thumbDimensions.DESKTOP_MEDIUM}px`,
      height: thumbDimensions.DESKTOP_MEDIUM,
    },
  },
  [theme.breakpoints.down('sl')]: {
    actionsHolder: {
      flexDirection: 'row',
    },
  },
  [theme.breakpoints.down('sm')]: {
    wrapper: {
      margin: '40px 20px',
      marginLeft: '0',
    },
    headerContent: {
      padding: `0 20px ${cssVariable.gutter}px 20px`,
    },
    flex: {
      alignItems: 'initial',
    },
    hideDownSm: {
      display: 'none',
    },
    showDownSm: {
      display: 'block',
    },
    buttonWrapper: {
      marginBottom: 10,
    },
    image: {
      flex: `0 0 ${thumbDimensions.DESKTOP_MEDIUM}px`,
      height: thumbDimensions.DESKTOP_MEDIUM,
    },
  },
  [theme.breakpoints.down('xs')]: {
    image: {
      flex: `0 0 ${thumbDimensions.MOBILE}px`,
      height: thumbDimensions.MOBILE,
    },
    podcastHeaderDescription: {
      fontSize: cssVariable.fontSizes.small,
    },
    actionsHolder: {
      flexDirection: 'column',
    },
  },

  // mobile header
  containerMobile: {
    marginTop: -30,
    display: 'flex',
    justifyContent: 'center',
    height: 230,
    overflow: 'hidden',
    marginBottom: 65,
  },

  backgroundImage: {
    backgroundColor: 'grey',
    backgroundPosition: '-45px -120px',
    backgroundSize: '120%',
    width: '100%',
    backgroundRepeat: 'no-repeat',
    filter: 'blur(8px)',
    opacity: '.4',
  },
  tileImage: {
    width: 235,
    height: 235,
    borderRadius: 8,
    marginTop: 30,
    position: 'absolute',
    backgroundSize: 'contain',
  },

  overflowEllipsis: {},
  descriptionContainer: {
    padding: '0 20px',
    marginBottom: 20,
    position: 'relative',
    fontSize: cssVariable.fontSizes.medium,
    color: cssVariable.grey500,

    '& $overflowEllipsis': {
      '@supports (-webkit-line-clamp: 4)': {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'initial',
        display: '-webkit-box',
        [cssVariable.WEBKIT_LINE_CLAMP]: 4,
        [cssVariable.WEBKIT_BOX_ORIENT]: 'vertical',
      },
    },
  },

  fullDescription: {
    '& $podcastHeaderDescription': {
      height: 'auto',
    },
  },
  podcastHeaderSubtitleMobile: {
    fontSize: cssVariable.fontSizes.small,
    margin: 0,
  },

  toggleCollapseBtn: {
    width: '100%',
    height: 40,
    color: cssVariable.blue500,
    background: cssVariable.white,
    border: 'none',
    cursor: 'auto',
  },
});

export default injectStyles(styles)(PodcastDetailPageHeader);
